import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

// 공용 말풍선 툴팁.
//
// native `title` 은 여러 줄·강조 표기가 안 되고 키보드 포커스로는 아예 뜨지
// 않는다. 설명이 한 줄을 넘는 자리(개조/변경점 칩, 자동 적용 스위치)는 이
// 컴포넌트를 쓴다.
//
// - 말풍선은 body 로 portal + position:fixed — 스크롤 컨테이너(overflow:auto)
//   안의 칩에서도 잘리지 않는다. 대신 스크롤/리사이즈 시에는 좌표가 낡으므로
//   그냥 닫는다.
// - hover 는 지연 후, keyboard focus 는 즉시 표시(포커스는 이미 의도적 동작).
// - Esc 는 document 레벨에서 받는다 — 포커스가 어디 있든 닫힌다.

/** 트리거와 말풍선 사이 간격. */
const GAP = 8;
/** 뷰포트 가장자리 최소 여백. */
const EDGE = 8;
/** hover 지연 — 커서가 스쳐 지나갈 때 깜빡이지 않게. */
const HOVER_DELAY_MS = 250;

interface TooltipProps {
  /** 말풍선 내용. 여러 줄/강조(<strong>) 가능. */
  content: ReactNode;
  children: ReactNode;
  /**
   * 트리거 wrapper 자체를 탭 정지점으로 만들지. 칩·배지처럼 포커스를 못 받는
   * 요소를 감쌀 때 true(기본). 안에 이미 버튼/체크박스가 있으면 false 로 두어
   * 탭 정지점이 두 번 생기는 것을 막는다(포커스 이벤트는 어차피 버블된다).
   */
  focusable?: boolean;
  /** 말풍선 최대 너비. */
  maxWidth?: number;
  /** 트리거 wrapper 인라인 스타일. */
  style?: CSSProperties;
  'data-testid'?: string;
}

export function Tooltip({
  content,
  children,
  focusable = true,
  maxWidth = 280,
  style,
  'data-testid': testId,
}: TooltipProps) {
  const tipId = useId();
  const triggerRef = useRef<HTMLSpanElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const hide = useCallback(() => {
    clearTimer();
    setOpen(false);
    setPos(null);
  }, [clearTimer]);

  const showNow = useCallback(() => {
    clearTimer();
    setOpen(true);
  }, [clearTimer]);

  const showDelayed = useCallback(() => {
    clearTimer();
    timerRef.current = setTimeout(() => setOpen(true), HOVER_DELAY_MS);
  }, [clearTimer]);

  useEffect(() => clearTimer, [clearTimer]);

  // 좌표 계산 — 기본 위쪽, 공간이 없으면 아래로 뒤집고 가로는 뷰포트 안으로 clamp.
  useLayoutEffect(() => {
    if (!open) return;
    const trigger = triggerRef.current;
    const tip = tipRef.current;
    if (!trigger || !tip) return;
    const t = trigger.getBoundingClientRect();
    const b = tip.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const above = t.top - b.height - GAP;
    const top =
      above >= EDGE ? above : Math.min(t.bottom + GAP, Math.max(EDGE, vh - b.height - EDGE));
    const centered = t.left + t.width / 2 - b.width / 2;
    const maxLeft = Math.max(EDGE, vw - b.width - EDGE);
    setPos({ top, left: Math.min(Math.max(centered, EDGE), maxLeft) });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') hide();
    }
    // capture — 모달 등 상위 Esc 핸들러가 먼저 먹기 전에 툴팁부터 닫는다.
    document.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('resize', hide);
    };
  }, [open, hide]);

  return (
    <span
      ref={triggerRef}
      style={{ ...triggerStyle, ...style }}
      tabIndex={focusable ? 0 : undefined}
      aria-describedby={open ? tipId : undefined}
      data-testid={testId}
      onMouseEnter={showDelayed}
      onMouseLeave={hide}
      onFocus={showNow}
      onBlur={hide}
    >
      {children}
      {open &&
        createPortal(
          <div
            ref={tipRef}
            id={tipId}
            role="tooltip"
            style={{
              ...bubbleStyle,
              maxWidth,
              top: pos?.top ?? 0,
              left: pos?.left ?? 0,
              // 측정 전 1프레임 동안 엉뚱한 자리에 번쩍이지 않게.
              opacity: pos ? 1 : 0,
            }}
          >
            {content}
          </div>,
          document.body,
        )}
    </span>
  );
}

const triggerStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  borderRadius: 'var(--radius-sm)',
};

const bubbleStyle: CSSProperties = {
  position: 'fixed',
  zIndex: 90,
  padding: '6px 10px',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-default)',
  background: 'var(--bg-raised)',
  color: 'var(--fg-default)',
  boxShadow: 'var(--shadow-lg)',
  fontSize: 12,
  lineHeight: 1.45,
  whiteSpace: 'pre-line',
  pointerEvents: 'none',
};
